/**
 * src/pages/Alertas.jsx
 * El archivo de alertas del sistema (tabla alertas_historial, Fase 3 SLA).
 *
 * Rediseño del 28-08 (hilo de distribución, decidido por Manuel):
 *   · El subtítulo explica la relación con el parte de Estado — ahí lo
 *     vigente de 24 h sin resolver, aquí el archivo completo (era la D5).
 *   · La resolución se ve: columna «Resuelta» (auto/manual y cuándo, desde la
 *     migración 033) y píldoras todas / vigentes / resueltas.
 *   · La celda de cliente lleva a su ficha (era la F4).
 *   · El filtro de categorías está completo: también Cobro, Billing, Lead y
 *     Onboarding, que el sistema emite y aquí no se podían filtrar.
 */
import { Fragment, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Header } from '../components/layout/Header'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Pildora } from '../components/ui/Pildora'
import { Table } from '../components/ui/Table'
import { Select } from '../components/ui/Input'
import { SelectorTenant } from '../components/ejecuciones/SelectorTenant'
import { useAlertas, useResolverAlertaEstado } from '../hooks/useAdminPanel'
import { useClientes } from '../hooks/useClientes'
import { fechaHoraCL } from '../lib/horaChile'

const SEV_COLOR = { critico: 'red', alerta: 'yellow', aviso: 'blue', info: 'gray' }

// El detalle de la alerta: lo que el vigía MIDIÓ y el «Se arregla» que eligió.
//
// ⚠️ 11-09-2026. Esto se guarda desde el 07-09 y **no se veía en ninguna parte**.
// Y no era que la pantalla no lo pintara: el endpoint no lo devolvía, así que el
// dato no llegaba al navegador. El titular sin la medida es justo lo que el §10
// llama entregar donde su lector no mira — con el agravante de que aquí estaba
// medido, guardado y a un campo de distancia.
//
// El caso que lo hizo urgente: la resiembra de competidores emite su diff («qué
// competidores entran y cuáles salen») en `campos`, y Manuel no podía verlo.
function DetalleAlerta({ campos }) {
  const filas = Object.entries(campos || {})
  if (!filas.length) return null
  return (
    <dl style={{
      margin: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr',
      gap: '6px var(--space-3)', fontSize: 'var(--text-sm)', alignItems: 'baseline',
    }}>
      {filas.map(([clave, valor]) => (
        <Fragment key={clave}>
          <dt style={{
            fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
            color: 'var(--color-text-3)', whiteSpace: 'nowrap',
          }}>{clave}</dt>
          {/* Un objeto o una lista se enseñan como JSON legible en vez de como
              «[object Object]», que es la forma de decir «hay algo aquí» sin
              decir qué. */}
          <dd style={{ margin: 0, color: 'var(--color-text)', whiteSpace: 'pre-wrap' }}>
            {typeof valor === 'string' || typeof valor === 'number'
              ? String(valor)
              : JSON.stringify(valor, null, 2)}
          </dd>
        </Fragment>
      ))}
    </dl>
  )
}

// ¿Tiene esta alerta algo que enseñar? Una sola regla, usada por la columna (para
// pintar el indicador) y por la tabla (para decidir si la fila se abre). Si cada
// una lo decidiera por su cuenta, habría filas con flecha que no abren nada (§4).
const _tieneDetalle = a => !!a?.campos && Object.keys(a.campos).length > 0

const SEV_OPTS = [
  { value: '', label: 'Todas las severidades' },
  { value: 'critico', label: 'Crítico' },
  { value: 'alerta', label: 'Alerta' },
  { value: 'aviso', label: 'Aviso' },
  { value: 'info', label: 'Info' },
]

const CAT_OPTS = [
  { value: '', label: 'Todas las categorías' },
  { value: 'PIPELINE', label: 'Pipeline' },
  { value: 'SLA', label: 'SLA' },
  { value: 'SALUD', label: 'Salud' },
  { value: 'SEGURIDAD', label: 'Seguridad' },
  { value: 'COBRO', label: 'Cobro' },
  { value: 'BILLING', label: 'Billing' },
  { value: 'LEAD', label: 'Lead' },
  { value: 'ONBOARDING', label: 'Onboarding' },
]

const ESTADOS = [
  { clave: '', etiqueta: 'todas' },
  { clave: 'vigentes', etiqueta: 'vigentes' },
  { clave: 'resueltas', etiqueta: 'resueltas' },
]

const PAGE = 50

/** Cierre manual desde el archivo (Manuel, 28-08): también las que ya
    caducaron del parte. Cerrar en falso no rompe nada — el vigía la vuelve a
    levantar en su siguiente pasada. */
function BotonResolver({ id }) {
  const resolver = useResolverAlertaEstado()
  return (
    <button
      onClick={() => resolver.mutate(`alerta_${id}`)}
      disabled={resolver.isPending}
      style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
        color: 'var(--color-text-3)', background: 'transparent',
        border: '1px solid var(--color-border-2)', borderRadius: 'var(--radius-sm)',
        padding: '2px 8px', cursor: 'pointer',
        transition: 'all var(--t-base) var(--ease)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.color = 'var(--color-ok)'
        e.currentTarget.style.borderColor = 'var(--color-ok-line)'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.color = 'var(--color-text-3)'
        e.currentTarget.style.borderColor = 'var(--color-border-2)'
      }}
    >
      {resolver.isPending ? 'Cerrando…' : '✓ Marcar resuelta'}
    </button>
  )
}

function _fechaCorta(iso) {
  return fechaHoraCL(iso, {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}

const COLUMNS = (navigate) => [
  {
    key: 'creado_en', label: 'Fecha', mono: true, nowrap: true, width: '130px',
    render: v => _fechaCorta(v) || '—',
  },
  {
    key: 'severidad', label: 'Severidad', width: '100px',
    render: v => <Badge color={SEV_COLOR[v] || 'gray'} dot>{v}</Badge>,
  },
  { key: 'categoria', label: 'Categoría', mono: true, width: '110px' },
  {
    key: 'titulo', label: 'Alerta',
    render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>
          {v}
          {/* El indicador solo aparece si hay algo que abrir: una flecha que no
              lleva a ninguna parte enseña a ignorar las que sí llevan. */}
          {_tieneDetalle(row) && (
            <span title="Tiene el detalle de lo que se midió — pulsa la fila"
                  style={{ marginLeft: '6px', fontSize: 'var(--text-xs)',
                           color: 'var(--color-text-3)' }}>▾</span>
          )}
        </div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)' }}>{row.resumen}</div>
      </div>
    ),
  },
  {
    key: 'cliente_id', label: 'Cliente', mono: true, width: '110px',
    // A la ficha del cliente (Manuel, 28-08): la celda era texto plano y desde
    // una alerta no se podía saltar a quien la sufre.
    render: v => v
      ? (
        <button
          onClick={() => navigate(`/clientes/${v}`)}
          title="Ver su ficha"
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
            color: 'var(--color-accent)', textDecoration: 'underline',
            textUnderlineOffset: '3px',
          }}
        >
          {v}
        </button>
      )
      : <span style={{ color: 'var(--color-text-3)' }}>—</span>,
  },
  {
    key: 'resuelta_en', label: 'Resuelta', width: '150px', mono: true,
    render: (v, row) => v
      ? (
        <span style={{ color: 'var(--color-ok)' }} title={`cerrada en ${row.resuelta_por === 'auto' ? 'el re-chequeo' : 'manual'}`}>
          {row.resuelta_por || '—'} · {_fechaCorta(v)}
        </span>
      )
      : <BotonResolver id={row.id} />,
  },
  {
    key: 'enviada_telegram', label: 'Canales', width: '90px', mono: true,
    render: (v, row) => {
      const canales = [v && 'TG', row.enviada_email && 'email'].filter(Boolean)
      return canales.length
        ? canales.join(' + ')
        : <span style={{ color: 'var(--color-text-3)' }}>ninguno</span>
    },
  },
]

export default function Alertas() {
  const navigate = useNavigate()
  // El filtro llega también por la URL: la atención de seguridad del parte
  // aterriza aquí ya filtrada (?categoria=SEGURIDAD) — Manuel, 27-08.
  const [searchParams] = useSearchParams()
  const [severidad, setSeveridad] = useState('')
  const [categoria, setCategoria] = useState(searchParams.get('categoria') || '')
  const [estado, setEstado] = useState('')
  const [cliente, setCliente] = useState(null)
  const [offset, setOffset] = useState(0)
  const [abierta, setAbierta] = useState(null)
  const { data, isLoading } = useAlertas({
    severidad, categoria, estado, cliente: cliente || '', limit: PAGE, offset,
  })
  // El backend siempre aceptó ?cliente= y la interfaz nunca lo usó (28-08).
  const { data: clientesRaw } = useClientes(true)
  const tenants = (Array.isArray(clientesRaw) ? clientesRaw : [])
    .map(c => ({ cliente_id: c.cliente_id, nombre: c.nombre, convenio: c.convenio }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))

  // `total` puede no llegar. Se separa de la aritmética de paginación: para
  // contar páginas hace falta un número, pero para DECIR cuántas alertas hay
  // hace falta haberlas contado — y «0 alertas» tras un fallo de lectura es la
  // versión tranquilizadora de «no lo sé».
  const total = data?.total ?? null
  const pagina = Math.floor(offset / PAGE) + 1
  const paginas = Math.max(1, Math.ceil((total ?? 0) / PAGE))
  // Sin total no se sabe si queda página siguiente, así que no se ofrece:
  // un «→» que no lleva a ninguna parte es peor que un «→» apagado.
  const finDeLista = total === null || offset + PAGE >= total

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title="Alertas"
        subtitle={
          'El parte de Estado enseña lo vigente (últimas 24 h, sin resolver); este es el archivo completo'
          + (total === null ? '' : ` · ${total.toLocaleString('es-CL')} registradas`)
        }
        action={
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <SelectorTenant tenants={tenants} valor={cliente}
                            onCambio={id => { setCliente(id); setOffset(0) }} />
            <Select value={severidad} options={SEV_OPTS}
                    onChange={e => { setSeveridad(e.target.value); setOffset(0) }} />
            <Select value={categoria} options={CAT_OPTS}
                    onChange={e => { setCategoria(e.target.value); setOffset(0) }} />
          </div>
        }
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: '14px' }}>
          {ESTADOS.map(f => (
            <Pildora key={f.etiqueta} activa={estado === f.clave}
                     onClick={() => { setEstado(f.clave); setOffset(0) }}>
              {f.etiqueta}
            </Pildora>
          ))}
        </div>

        <Card padding="0">
          <Table
            columns={COLUMNS(navigate)}
            data={data?.alertas}
            loading={isLoading}
            // Solo se abre lo que tiene algo dentro, con la MISMA regla que
            // decide el indicador (§4). Volver a pulsar cierra.
            onRowClick={row => _tieneDetalle(row)
              && setAbierta(a => (a === row.id ? null : row.id))}
            claveExpandida={abierta}
            renderExpandida={row => <DetalleAlerta campos={row.campos} />}
            emptyMessage={estado
              ? 'Ninguna alerta en este filtro'
              : 'Sin alertas registradas (el historial arranca con el deploy de la Fase 3)'}
          />
        </Card>

        {paginas > 1 && (
          <div style={{
            marginTop: 'var(--space-3)', display: 'flex', gap: '10px', alignItems: 'center',
            justifyContent: 'flex-end', fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)',
            color: 'var(--color-text-2)',
          }}>
            <button disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - PAGE))}
              style={{
                border: '1px solid var(--color-border)', background: 'var(--color-surface)',
                borderRadius: 'var(--radius-sm)', padding: '3px 9px',
                cursor: offset === 0 ? 'default' : 'pointer',
                color: offset === 0 ? 'var(--color-text-3)' : 'var(--color-text-2)',
              }}>←</button>
            <span>página {pagina} de {paginas}</span>
            <button disabled={finDeLista} onClick={() => setOffset(o => o + PAGE)}
              style={{
                border: '1px solid var(--color-border)', background: 'var(--color-surface)',
                borderRadius: 'var(--radius-sm)', padding: '3px 9px',
                cursor: finDeLista ? 'default' : 'pointer',
                color: finDeLista ? 'var(--color-text-3)' : 'var(--color-text-2)',
              }}>→</button>
          </div>
        )}
      </div>
    </div>
  )
}
